import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(username, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка входа');
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-[var(--app-bg)]">
      <div className="pointer-events-none absolute inset-0 -z-20 bg-[radial-gradient(circle_at_top_right,rgba(59,104,255,0.18),transparent_60%)]" />
      <div className="pointer-events-none absolute inset-0 -z-30 bg-[radial-gradient(circle_at_bottom_left,rgba(30,64,175,0.15),transparent_65%)]" />

      <div className="absolute top-6 right-6 hidden lg:flex">
        <ThemeToggle size="sm" />
      </div>

      <div className="flex w-full flex-col lg:flex-row">
        <div className="relative hidden w-full max-w-xl flex-1 flex-col justify-between overflow-hidden bg-gradient-to-br from-primary-700 via-primary-500 to-indigo-500 px-12 py-14 text-white shadow-lifted lg:flex">
          <div>
            <div className="flex items-center gap-3 text-lg font-semibold">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-lg font-bold shadow-inner ring-1 ring-white/20">
                PM
              </span>
              Pay Module Admin
            </div>
            <p className="mt-8 max-w-sm text-base leading-relaxed text-white/80">
              Новый визуальный стиль помогает сосредоточиться на ключевых метриках и ускоряет работу с платежами,
              выводами и запросами магазинов.
            </p>
          </div>

          <div className="space-y-5">
            <div className="rounded-3xl border border-white/20 bg-white/10 p-6 backdrop-blur-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/70">
                Возможности платформы
              </p>
              <ul className="mt-4 space-y-3 text-sm text-white/80">
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-white/70" />
                  Реальное время обновления статусов платежей
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-white/70" />
                  Умный поиск по клиентам, транзакциям и операторам
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-white/70" />
                  Централизованное управление магазинами и пользователями
                </li>
              </ul>
            </div>
            <p className="text-xs text-white/60">© {currentYear} Pay Module. Все права защищены.</p>
          </div>
        </div>

        <div className="relative flex flex-1 items-center justify-center px-4 py-12 sm:px-8 lg:px-16">
          <div className="absolute top-6 right-6 lg:hidden">
            <ThemeToggle size="sm" />
          </div>

          <div className="w-full max-w-md space-y-8 rounded-3xl border border-[var(--app-border)] bg-[var(--app-surface)]/95 p-8 shadow-lifted backdrop-blur-sm sm:p-10">
            <div className="space-y-3 text-center">
              <span className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--app-surface-subtle)] px-3 py-1 text-xs font-semibold text-[var(--app-text-muted)]">
                Платежная система
              </span>
              <h1 className="text-3xl font-bold text-[var(--app-heading)]">Добро пожаловать 👋</h1>
              <p className="text-sm text-[var(--app-text-muted)]">
                Авторизуйтесь, чтобы управлять платежами, выводами и командами магазина в обновленной админ-панели.
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <label
                  htmlFor="username"
                  className="flex items-baseline justify-between text-sm font-medium text-[var(--app-text)]"
                >
                  Имя пользователя
                  <span className="text-xs font-normal text-[var(--app-text-muted)]">Корпоративный логин</span>
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  className="block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-4 py-3 text-[var(--app-text)] shadow-sm transition focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                  placeholder="Введите логин"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="flex items-baseline justify-between text-sm font-medium text-[var(--app-text)]"
                >
                  Пароль
                  <span className="text-xs font-normal text-[var(--app-text-muted)]">Минимум 8 символов</span>
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="block w-full rounded-2xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-4 py-3 text-[var(--app-text)] shadow-sm transition focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                  placeholder="Введите пароль"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>

              {error ? (
                <div className="rounded-2xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={loading}
                className="relative inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? 'Входим...' : 'Войти'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
